import { makeAutoObservable, runInAction } from "mobx";

import { authStore } from "@/features/auth";
import { companyStore } from "@/features/company";

import { onboardingService } from "../services";
import { normalizeOnboardPayload } from "../utils";

const createYearOptions = (startYear, endYear) => {
  const list = [];
  for (let year = startYear; year <= endYear; year += 1) {
    list.push(year);
  }
  return list;
};

class OnboardingStore {
  loading = false;
  saving = false;
  onboardData = null;
  error = null;
  currentCompanyId = null;
  _fetchPromise = null;

  currentYear = new Date().getFullYear();
  nextYear = new Date().getFullYear() + 1;
  baseYearOptions = [];
  targetYearOptions = [];
  reportYearOptions = [];

  constructor() {
    makeAutoObservable(this);
    this.getYearOptions();
    this.getReportYearOptions();
  }

  getYearOptions = () => {
    this.baseYearOptions = createYearOptions(2020, this.currentYear);
    this.targetYearOptions = createYearOptions(
      this.nextYear,
      this.nextYear + 9,
    );
  };

  getReportYearOptions = () => {
    this.reportYearOptions = createYearOptions(2020, this.currentYear);
    this.targetYearOptions = createYearOptions(
      this.nextYear,
      this.nextYear + 9,
    );
  };

  resolveCompanyId = (options = {}) => {
    const overrideId =
      options?.companyId ?? options?.companyID ?? options?.id ?? null;
    if (overrideId) return overrideId;
    return (
      authStore?.user?.companyId ??
      companyStore.company?._id ??
      companyStore.company?.id ??
      companyStore.company?.companyId ??
      null
    );
  };

  fetchOnboard = async (input) => {
    const options =
      typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : { companyId: input };

    const force = Boolean(options?.force);
    const companyId = this.resolveCompanyId(options);

    if (!force && this._fetchPromise) {
      return this._fetchPromise;
    }

    if (!companyId) {
      runInAction(() => {
        this.onboardData = null;
        this.loading = false;
        this.currentCompanyId = null;
      });
      return null;
    }

    this.loading = true;
    this.currentCompanyId = companyId;

    const request = onboardingService
      .fetchOnboard(companyId, { showErrorToast: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load onboarding details.");
        }
        runInAction(() => {
          this.onboardData = data;
        });
        return data;
      })
      .catch((err) => {
        runInAction(() => {
          this.error = err;
        });
        throw err;
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
          this._fetchPromise = null;
        });
      });

    this._fetchPromise = request;
    return request;
  };

  uploadScopeFiles = async (scopeKey, payload, companyId) => {
    const scopeData = payload?.[`${scopeKey}Emission`]?.data;
    if (!Array.isArray(scopeData) || scopeData.length === 0) return;

    const fileIndexes = [];
    const formData = new FormData();

    scopeData.forEach((entry, idx) => {
      if (entry?.file) {
        formData.append("files", entry.file, entry.file.name || `file-${idx}`);
        fileIndexes.push(idx);
      }
    });

    if (fileIndexes.length === 0) return;

    const data = await onboardingService.uploadFilesBatch(companyId, formData, {
      showErrorToast: false,
    });

    if (!data) {
      throw new Error("Unable to upload reference files.");
    }

    const files = data?.files || data?.data?.files || data?.uploaded || [];
    files.forEach((fileDoc, i) => {
      const originalIndex = fileIndexes[i];
      if (originalIndex == null || !scopeData[originalIndex]) return;
      const target = scopeData[originalIndex];
      target.refFileName =
        fileDoc?.originalName || fileDoc?.name || target.refFileName;
      target.refFileLink = fileDoc?.url || fileDoc?.link || target.refFileLink;
      delete target.file;
    });
  };

  saveOnboard = async (project, payload, companyId) => {
    const resolvedCompanyId = this.resolveCompanyId({ companyId });
    if (!resolvedCompanyId) {
      throw new Error(
        "Cannot save onboarding data without an assigned company.",
      );
    }

    const normalizedProject = String(
      project || payload?.project || "",
    ).toLowerCase();
    const body = normalizeOnboardPayload(normalizedProject, payload);

    if (normalizedProject === "nxplan") {
      await this.uploadScopeFiles("scope1", body, resolvedCompanyId);
      await this.uploadScopeFiles("scope2", body, resolvedCompanyId);
      await this.uploadScopeFiles("scope3", body, resolvedCompanyId);
    }

    this.saving = true;
    try {
      const data = await onboardingService.saveOnboard(
        normalizedProject,
        resolvedCompanyId,
        body,
        { showErrorToast: false },
      );
      if (!data) {
        return false;
      }
      return data ?? true;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  reset = () => {
    this.onboardData = null;
    this.error = null;
    this._fetchPromise = null;
  };
}

export const onboardingStore = new OnboardingStore();
